import { Favorite } from "@/components/Favorite"
import { useSimplePdp } from "@/lib/preFetch/preFetchPdp"
import { usePromotionTag } from "@/lib/preFetch/prefetchPromoTag"
import prismicColors from "@/lib/prismicio/prismic-colors.json"
import {
    ColorCircle,
    Link,
    ListerTileImage,
    ListerTileProps,
    NextImage,
    PromotionalImage,
    cn,
    getColor,
    getProductUrl,
    getTweakwiseGrid,
    transMoney,
    useGallery,
    useI18n,
    useTile,
} from "@jog/react-components"
import { colorLanguageConverter, getGtmIdFromTW } from "@jog/shared"
import { TweakwiseItem } from "@jog/types"
import { type CSSProperties, Fragment } from "react"

const Price = ({ product: { isDiscount, price } }: { product: ReturnType<typeof useTile>["product"] }) => (
    <div className={cn(isDiscount ? "text-red-main" : "text-black", "font-primary text-sm font-bold lg:text-lg")}>
        {transMoney(price * 100)}
    </div>
)
interface CircleStyle extends CSSProperties {
    ["--backgroundColor"]: string
}

const Group = ({
    data,
    onClick,
    tile: { group, hasGroup, setActiveProduct },
}: {
    data: TweakwiseItem
    onClick: (data: TweakwiseItem) => void
    tile: Omit<ReturnType<typeof useTile>, "product" | "promotion">
}) => {
    if (!hasGroup) return <div className="h-6.75" />
    return (
        <div className="z-[2] -mr-0.5 flex h-6.75 max-w-2/3 items-center lg:space-x-0.5">
            {group.slice(0, 4).map((i) => {
                const color = getColor(i)
                const circleStyle: CircleStyle = {
                    "--backgroundColor": colorLanguageConverter(color, prismicColors.colors),
                    colorScheme: "only light",
                }

                const label = `${i.title} ${color}`
                const href = getProductUrl(i)
                const commonClass = `items-center justify-center`
                return (
                    <Fragment key={`${data.itemno}_${i.itemno}`}>
                        {/*mobile*/}
                        <Link
                            href={href}
                            onClick={(e) => {
                                e.stopPropagation()
                                onClick?.(i)
                            }}
                            aria-label={label}
                            className={cn(commonClass, "flex lg:hidden")}>
                            <ColorCircle
                                className="h-4 w-4 fill-[var(--backgroundColor)] stroke-grey-lighter"
                                style={circleStyle}
                            />
                        </Link>
                        {/*desktop*/}
                        <Link
                            href={href}
                            aria-label={label}
                            className={cn(commonClass, "hidden lg:flex")}
                            onMouseEnter={() => setActiveProduct(i)}
                            onMouseLeave={() => setActiveProduct(data)}
                            onClick={(e) => {
                                e.stopPropagation()
                                onClick?.(i)
                            }}>
                            <ColorCircle
                                className="h-5 w-5 fill-[var(--backgroundColor)] stroke-grey-lighter"
                                style={circleStyle}
                            />
                        </Link>
                    </Fragment>
                )
            })}
            {group.length > 4 ? <div className="mx-1.25 flex text-sm">{`+${group.length - 4}`}</div> : null}
        </div>
    )
}
const Discount = ({
    product: { isDiscount, firstPrice, lowestPrice, price },
}: {
    product: ReturnType<typeof useTile>["product"]
}) => {
    const { $t } = useI18n()
    if (!isDiscount) return <></>
    return (
        <div className="text-10 text-grey-dark">
            <p>{$t(`Original price: {price}`, { price: transMoney(+firstPrice * 100, false) })}</p>
            {price === lowestPrice || lowestPrice === +firstPrice ? null : (
                <p>{$t(`Lowest price last 30 days: {price}`, { price: `${transMoney(lowestPrice * 100, false)}` })}</p>
            )}
        </div>
    )
}
export const ListerTile = ({ data, className, group: _group, onClick }: ListerTileProps) => {
    const { product, promotion, ...tile } = useTile(data, _group)
    const promotionTag = usePromotionTag(promotion)
    const { mobileSortingByName, desktopSortingByName } = useSimplePdp()
    const gallery = useGallery(tile.activeProduct, mobileSortingByName, desktopSortingByName)

    // id={`item_${data.itemno}`} is for scroll to item, check useLastLister.tsx
    return (
        <div className={cn(className, "relative flex h-full flex-col bg-white")} id={`item_${data.itemno}`}>
            <div className="group relative">
                <ListerTileImage
                    name={product.name}
                    gallery={gallery}
                    onClick={() => onClick?.(data)}
                    href={product.linkItem}
                />
                <PromotionalImage
                    className="absolute -top-1.25 -right-1.25 w-17.5 object-cover"
                    promotionTag={promotionTag}
                />
                <Link
                    onClick={() => onClick?.(data)}
                    href={product.linkItem}
                    className="absolute inset-0 hidden lg:block"
                    aria-label={product.name}
                />
                <Favorite
                    productId={data.itemno}
                    name={product.name}
                    price={product.price}
                    gtmId={getGtmIdFromTW(data)}
                />
            </div>
            <div className="relative grid flex-1 grid-rows-[48px,36px,auto] px-2 pb-1.5 pt-2.75 lg:px-4">
                <div className="line-clamp-2 font-primary text-base font-normal uppercase text-gray-base">
                    {product.name}
                </div>
                <div className="mt-2 flex items-center justify-between">
                    <Price product={product} />
                    <Group tile={tile} onClick={onClick} data={data} />
                </div>
                <Discount product={product} />
                <Link
                    href={product.linkItem}
                    className="absolute inset-0"
                    aria-label={product.name}
                    onClick={() => onClick?.(data)}
                />
            </div>
        </div>
    )
}
export const VisualTile = ({ item }: { item: TweakwiseItem }) => {
    const { grid, aspectRatio } = getTweakwiseGrid(item)
    return (
        <div className={grid}>
            <Link
                href={item.url}
                className={cn("flex h-full w-full flex-col", "md:aspect-[var(--desktop-aspect-ratio)]")}
                aria-label={item.title}
                style={aspectRatio}>
                <NextImage alt="" height={400} width={2000} className="h-full w-full object-cover" src={item.image} />
            </Link>
        </div>
    )
}
